import { STANDARD_BRANCH } from '@festina-life/api'
import { dateUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { FilterCheckbox } from '@festina-life/ui/listsAndTables'
import { Stack } from '@festina-life/ui/structure'
import cx from 'classnames'

import { DraftIndicator } from '../../components/draftIndicator/DraftIndicator'
import { OperationApproval } from '../../components/operationApproval/OperationApproval'
import { OperationViewerTopBar } from '../../components/operationViewerTopBar/OperationViewerTopBar'
import { useConfigurationContext } from '../../context/configurationContext'
import { useDraftContext } from '../../context/draftContext'
import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useIsPersonaEditor } from '../../uiModels/useIsPersonaEditor'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { TemplateNavigationControls } from './TemplateNavigationControls'
import { TemplatePreviousVersionDiffSelector } from './TemplatePreviousVersionDiffSelector'
import { TemplateTransferButton } from './TemplateTransferButton'
import { TemplateViewPreferencesMenu } from './TemplateViewPreferencesMenu'

/**
 * TopBar used to configure the TemplateViewer view. Will provide controls for searching, exporting, flattening the
 * view, etc.
 */
export const TemplateTopBar = () => {
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  const { applicationTemplates } = useApplicationViewOperationsContext()
  const { onConfigureNewDraft } = useDraftContext()
  const { preferences, onChangeView } = useLayoutContext()
  const { viewAt } = useConfigurationContext()
  const translate = useTranslate()
  const { branch, layer } = useNavigationParams()
  const isEditor = useIsPersonaEditor()
  const areControlsVisible = !templateVersion?.versionInfo.isHideVersion
  const currentTemplateModelTypeName =
    templateVersion?.modelTypeName ??
    applicationTemplates.get(templateVersion?.versionInfo.identifier ?? '')
      ?.lastEntity?.modelTypeName ??
    ''

  const currentTemplatePackagePath = templateVersion?.versionInfo.package
    ?.length
    ? templateVersion.versionInfo.package
    : (applicationTemplates
        ?.get(templateVersion?.versionInfo.identifier ?? '')
        ?.lastEntity?.packageName?.split('.') ?? [])

  const changesetKey =
    templateVersion?.versionInfo.changesetHistory?.changesetKey
  const operationKey = templateVersion?.versionInfo.operationKey

  return (
    <OperationViewerTopBar
      className={cx(
        'mb-2',
        'ps-7', // to prevent colliding with the CollapsibleHandleButton
        areControlsVisible ? 'justify-between' : 'justify-end',
      )}
    >
      {areControlsVisible && (
        <Stack className="items-center" gap="display">
          <FilterCheckbox
            label={translate('templateViewer.topBar.option.flatten')}
            id="flatten"
            checked={preferences.templates.isFlattened}
            onChange={() =>
              onChangeView({
                ...preferences,
                templates: {
                  ...preferences.templates,
                  isFlattened: !preferences.templates.isFlattened,
                },
              })
            }
          />
          <FilterCheckbox
            label={translate('templateViewer.topBar.option.showEmpty')}
            id="showEmpty"
            checked={preferences.templates.isShowingEmptyValues}
            onChange={() =>
              onChangeView({
                ...preferences,
                templates: {
                  ...preferences.templates,
                  isShowingEmptyValues:
                    !preferences.templates.isShowingEmptyValues,
                },
              })
            }
          />
          <TemplatePreviousVersionDiffSelector />
          <TemplateViewPreferencesMenu />
        </Stack>
      )}

      <Stack className="items-center" gap="spacious">
        <TemplateNavigationControls />
        {!isDraftTemplate &&
          branch !== STANDARD_BRANCH &&
          changesetKey &&
          operationKey && (
            <OperationApproval
              actions={templateVersion?.versionInfo.actions ?? []}
              changesetKey={changesetKey}
              operationKey={operationKey}
            />
          )}
        {isEditor && branch !== STANDARD_BRANCH && (
          <Button
            startIcon="DocumentDuplicate"
            onClick={() => {
              onConfigureNewDraft(
                {
                  type: 'template',
                  value: {
                    packagePath: currentTemplatePackagePath,
                    templateId: templateVersion?.versionInfo.identifier ?? '',
                    fromDate: dateUtils.dateAsServerStr(viewAt),
                    modelTypeName: currentTemplateModelTypeName,
                    isHideOperation: false,
                    variant: 'copy',
                  },
                },
                templateVersion?.layer ?? layer,
              )
            }}
          >
            {translate('templateViewer.topBar.actions.copy')}
          </Button>
        )}
        <TemplateTransferButton />
        {isDraftTemplate && <DraftIndicator />}
      </Stack>
    </OperationViewerTopBar>
  )
}
